import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Badge, Box, Stack, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  useGetGlobalFilterQuery,
  useGetPractitionersByClinicIdQuery,
  useUpdateGlobalFilterMutation
} from '@/store/slices/api/userApiSlice';
import { setFilters as setGlobalFilters } from '@/store/slices/preferencesSlice';
import { delay, fetchActiveFiltersCount } from '@/utils';
import { NULL_OPTION } from '@/utils/constants';
import useSaveUpdatedReportFields from '@/hooks/useSaveUpdatedReportFields';
import MultiSelectDropdownWithSearch from '@/components/MultiSelectDropdownWithSearch';

import { LabelAndValue } from '@/types/custom';
import { DeviceType, DeviceVendor, getVendorDisplayName } from '@/types/matchDevice';
import { UserRole } from '@/types/user';
import CustomModal from '@/ui/Modal';

interface FiltersModalProps {
  iconStyle?: React.CSSProperties;
  customFilterIcon?: React.ReactNode;
}

export interface FilterState {
  clinics: LabelAndValue[];
  practitioners: LabelAndValue[];
  signingPractitioners: LabelAndValue[];
  deviceTypes: LabelAndValue[];
  vendors: LabelAndValue[];
}

const FilterOption = memo(
  ({
    options,
    defaultValues,
    onChange,
    label,
    isLoading,
    showTooltip,
    tooltipMessage,
    infoMessage
  }: {
    options: LabelAndValue[];
    defaultValues: LabelAndValue[];
    onChange: (values: LabelAndValue[]) => void;
    label: string;
    isLoading?: boolean;
    showTooltip?: boolean;
    tooltipMessage?: string;
    infoMessage?: string | null;
  }) => (
    <MultiSelectDropdownWithSearch
      options={options}
      defaultValues={defaultValues}
      onChange={onChange}
      label={label}
      maxItemsToDisplay={1}
      isLoading={isLoading}
      showTooltip={showTooltip}
      tooltipMessage={tooltipMessage}
      infoMessage={infoMessage}
    />
  )
);

FilterOption.displayName = 'FilterOption';

const DEVICE_TYPE_OPTIONS: LabelAndValue[] = Object.values(DeviceType).map((deviceType) => ({
  label: deviceType,
  value: deviceType
}));
DEVICE_TYPE_OPTIONS.unshift(NULL_OPTION);

const VENDOR_OPTIONS: LabelAndValue[] = Object.values(DeviceVendor).map((vendor) => ({
  label: getVendorDisplayName(vendor),
  value: vendor
}));

const FiltersModal = ({ iconStyle, customFilterIcon }: FiltersModalProps) => {
  const dispatch = useAppDispatch();
  const [opened, { open, close }] = useDisclosure();
  const { filters: globalFilters } = useAppSelector((state) => state.preferences);
  const { reportUpdateFields } = useAppSelector((state) => state.transmissionReports);
  const { user } = useAppSelector((state) => state.auth);
  const saveUpdatedReportFields = useSaveUpdatedReportFields();

  const userRole = user?.role;
  const isUserAdmin =
    userRole === UserRole.InternalSuperAdmin ||
    userRole === UserRole.InternalAdmin ||
    userRole === UserRole.InternalNonClinical ||
    userRole === UserRole.InternalClinical;

  const [updateGlobalFilter, { isLoading: isUpdatingFilter }] = useUpdateGlobalFilterMutation();

  const {
    defaultClinics,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultDeviceTypes,
    defaultVendors
  } = useMemo(() => {
    if (!globalFilters)
      return {
        defaultClinics: [],
        defaultPractitioners: [],
        defaultSigningPractitioners: [],
        defaultDeviceTypes: [],
        defaultVendors: []
      };
    return {
      defaultClinics: globalFilters.clinics,
      defaultPractitioners: globalFilters.practitioners,
      defaultSigningPractitioners: globalFilters.signingPractitioners ?? [],
      defaultDeviceTypes: globalFilters.deviceTypes,
      defaultVendors: globalFilters.vendors
    };
  }, [globalFilters]);

  const { data: globalFilterData } = useGetGlobalFilterQuery();

  const [filters, setFilters] = useState<FilterState>({
    clinics: defaultClinics,
    practitioners: defaultPractitioners,
    signingPractitioners: defaultSigningPractitioners,
    deviceTypes: defaultDeviceTypes,
    vendors: defaultVendors
  });

  const {
    data: practitionerData,
    isLoading: practitionerOptionsLoading,
    isFetching: practitionerOptionsFetching
  } = useGetPractitionersByClinicIdQuery({
    clinicIds: filters.clinics.map((clinic) => clinic.value)
  });

  useEffect(() => {
    setFilters({
      clinics: defaultClinics,
      practitioners: defaultPractitioners,
      signingPractitioners: defaultSigningPractitioners,
      deviceTypes: defaultDeviceTypes,
      vendors: defaultVendors
    });
  }, [
    defaultClinics,
    defaultDeviceTypes,
    defaultPractitioners,
    defaultSigningPractitioners,
    defaultVendors
  ]);

  // Memoized clinic options
  const clinicOptions = useMemo(
    () =>
      globalFilterData?.clinics
        .map((clinic) => ({
          label: clinic.name,
          value: clinic.id,
          desc: clinic?.parent?.name ?? ''
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
    [globalFilterData]
  );

  // Check if clinic options have multiple accounts
  const hasMultipleAccountsInOptions = useMemo(() => {
    const accounts = new Set(clinicOptions.map((clinic) => clinic.desc || 'No Account'));
    return accounts.size > 1;
  }, [clinicOptions]);

  const practitionerOptions = useMemo(() => {
    const newPractitionerOptions =
      practitionerData?.data.map((practitioner) => ({
        label: practitioner.name,
        value: practitioner.id
      })) ?? [];

    // Remove duplicates and sort alphabetically
    const options = [...newPractitionerOptions]
      .reduce(
        (acc, current) =>
          acc.some((item) => item.value === current.value) ? acc : [...acc, current],
        [] as LabelAndValue[]
      )
      .sort((a, b) => a.label.localeCompare(b.label));
    options.unshift(NULL_OPTION);
    return options;
  }, [practitionerData]);

  const updateFilter = useCallback(
    (key: keyof FilterState) => (values: LabelAndValue[]) => {
      if (key === 'clinics' && !isUserAdmin && hasMultipleAccountsInOptions) {
        // Check if the newly selected clinics include options from different accounts
        const selectedAccounts = new Set(
          values.map((clinic) => {
            const option = clinicOptions.find((opt) => opt.value === clinic.value);
            return option?.desc || 'No Account';
          })
        );

        // If multiple accounts are selected, keep only the most recently selected clinic
        if (selectedAccounts.size > 1) {
          // Find the most recently added clinic (the one not in the previous selection)
          const previousValues = filters.clinics;
          const newClinic = values.find(
            (v) => !previousValues.some((prev) => prev.value === v.value)
          );

          if (newClinic) {
            // Get the account of the newly selected clinic
            const newAccount =
              clinicOptions.find((opt) => opt.value === newClinic.value)?.desc || 'No Account';

            // Filter to keep only clinics from the same account as the newly selected one
            const filteredClinics = values.filter((clinic) => {
              const option = clinicOptions.find((opt) => opt.value === clinic.value);
              const account = option?.desc || 'No Account';
              return account === newAccount;
            });

            setFilters((prev) => ({
              ...prev,
              practitioners: [],
              signingPractitioners: [],
              clinics: filteredClinics
            }));
            return;
          }
        }
      }

      setFilters((prev) => ({
        ...prev,
        // Clear selected practitioners when clinics change
        practitioners: key === 'clinics' ? [] : prev.practitioners,
        signingPractitioners: key === 'clinics' ? [] : prev.signingPractitioners,
        [key]: values
      }));
    },
    [clinicOptions, filters.clinics, isUserAdmin, hasMultipleAccountsInOptions]
  );

  const handleApply = useCallback(async () => {
    try {
      await updateGlobalFilter({
        organizationIds: filters.clinics.map((c) => c.value),
        practitionerIds: filters.practitioners.map((p) => p.value),
        deviceTypes: filters.deviceTypes.map((d) => d.value),
        vendors: filters.vendors.map((v) => v.value),
        signingPractitionerIds: filters.signingPractitioners.map((s) => s.value),
        patientStatuses: globalFilters?.patientStatuses?.map((s) => s.value) ?? []
      }).unwrap();

      dispatch(
        setGlobalFilters({
          clinics: filters.clinics,
          practitioners: filters.practitioners,
          signingPractitioners: filters.signingPractitioners,
          deviceTypes: filters.deviceTypes,
          vendors: filters.vendors,
          patientStatuses: globalFilters?.patientStatuses ?? []
        })
      );

      close();
    } catch (error) {
      console.error({
        message: 'Failed to update global filter',
        error
      });
      showNotification({
        title: 'Error',
        message: 'Failed to update global filter',
        color: 'red'
      });
    }
  }, [
    updateGlobalFilter,
    dispatch,
    filters.clinics,
    filters.practitioners,
    filters.signingPractitioners,
    filters.deviceTypes,
    filters.vendors,
    globalFilters?.patientStatuses,
    close
  ]);

  const isApplyDisabled = useMemo(() => {
    return (
      filters.clinics.length === 0 &&
      filters.practitioners.length === 0 &&
      filters.signingPractitioners.length === 0 &&
      filters.deviceTypes.length === 0 &&
      filters.vendors.length === 0 &&
      defaultClinics.length === 0 &&
      defaultPractitioners.length === 0 &&
      defaultSigningPractitioners.length === 0 &&
      defaultDeviceTypes.length === 0 &&
      defaultVendors.length === 0
    );
  }, [
    defaultClinics.length,
    defaultDeviceTypes.length,
    defaultPractitioners.length,
    defaultSigningPractitioners.length,
    defaultVendors.length,
    filters.clinics.length,
    filters.deviceTypes.length,
    filters.practitioners.length,
    filters.signingPractitioners.length,
    filters.vendors.length
  ]);

  const activeFiltersCount = useMemo(() => {
    if (!globalFilters) return 0;
    const { patientStatuses: _, ...rest } = globalFilters;
    return fetchActiveFiltersCount(rest as typeof globalFilters);
  }, [globalFilters]);

  const onCloseClick = () => {
    close();
    setFilters({
      clinics: defaultClinics,
      practitioners: defaultPractitioners,
      signingPractitioners: defaultSigningPractitioners,
      deviceTypes: defaultDeviceTypes,
      vendors: defaultVendors
    });
  };

  const clearAllFiltersClick = () => {
    setFilters({
      clinics: [],
      practitioners: [],
      signingPractitioners: [],
      deviceTypes: [],
      vendors: []
    });
  };

  const isClearAllDisabled = useMemo(() => {
    return (
      filters.clinics.length === 0 &&
      filters.practitioners.length === 0 &&
      filters.signingPractitioners.length === 0 &&
      filters.deviceTypes.length === 0 &&
      filters.vendors.length === 0
    );
  }, [filters]);

  const onOpenClick = async () => {
    if (reportUpdateFields && Object.keys(reportUpdateFields).length > 0) {
      saveUpdatedReportFields();
      await delay(1000);
    }
    open();
  };

  return (
    <>
      <Tooltip label="Filters" position="right" withArrow>
        <Box pos={'relative'} onClick={onOpenClick} style={{ marginTop: 7, cursor: 'pointer' }}>
          {customFilterIcon ?? (
            <IconAdjustmentsHorizontal
              style={{
                ...iconStyle
              }}
            />
          )}
          {activeFiltersCount > 0 && (
            <Badge variant="filled" ml={5} size="xs" circle pos={'absolute'} top={-8} right={-5}>
              {activeFiltersCount}
            </Badge>
          )}
        </Box>
      </Tooltip>

      <CustomModal
        header={{
          title: 'Filters'
        }}
        modalProps={{
          opened,
          onClose: onCloseClick,
          centered: true,
          closeOnClickOutside: false
        }}
        primaryButton={{
          text: 'Apply',
          onClick: handleApply,
          isDisabled: isApplyDisabled,
          isLoading: isUpdatingFilter
        }}
        secondaryButton={{
          text: 'Cancel',
          onClick: onCloseClick
        }}
        actionButton={{
          text: 'Clear All',
          onClick: clearAllFiltersClick,
          isDisabled: isClearAllDisabled
        }}>
        <Stack gap="md" p="md">
          <FilterOption
            options={clinicOptions}
            defaultValues={filters.clinics}
            onChange={updateFilter('clinics')}
            label="Clinics"
            showTooltip={!isUserAdmin && hasMultipleAccountsInOptions}
            tooltipMessage="Selecting a clinic from a different account will clear your previous clinic selection"
            infoMessage={
              !isUserAdmin && hasMultipleAccountsInOptions
                ? 'Selecting a clinic in another account resets your selection'
                : null
            }
          />
          <FilterOption
            options={practitionerOptions}
            defaultValues={filters.practitioners}
            onChange={updateFilter('practitioners')}
            label="Following Practitioners"
            isLoading={practitionerOptionsLoading || practitionerOptionsFetching}
          />
          <FilterOption
            options={practitionerOptions}
            defaultValues={filters.signingPractitioners}
            onChange={updateFilter('signingPractitioners')}
            label="Signing Practitioners"
            isLoading={practitionerOptionsLoading || practitionerOptionsFetching}
          />
          <FilterOption
            options={DEVICE_TYPE_OPTIONS}
            defaultValues={filters.deviceTypes}
            onChange={updateFilter('deviceTypes')}
            label="Device Types"
          />
          <FilterOption
            options={VENDOR_OPTIONS}
            defaultValues={filters.vendors}
            onChange={updateFilter('vendors')}
            label="Vendors"
          />
        </Stack>
      </CustomModal>
    </>
  );
};

export default memo(FiltersModal);
